import { useEffect, useRef, useState } from 'react';

type Params = {
  isDisabled: boolean;
};

export const useStickyHorizontalScrollbar = ({ isDisabled }: Params) => {
  const tableRef = useRef<HTMLTableElement | null>(null);
  const tableWrapperRef = useRef<HTMLDivElement | null>(null);
  const stickyScrollbarRef = useRef<HTMLDivElement | null>(null);
  const stickyScrollbarContentRef = useRef<HTMLDivElement | null>(null);
  const isSyncingRef = useRef(false);
  const [hasOverflow, setHasOverflow] = useState(false);

  useEffect(() => {
    if (isDisabled) {
      return;
    }

    const table = tableRef.current;
    const tableWrapper = tableWrapperRef.current;
    const stickyScrollbar = stickyScrollbarRef.current;
    const stickyScrollbarContent = stickyScrollbarContentRef.current;

    if (!table || !tableWrapper || !stickyScrollbar || !stickyScrollbarContent) {
      return;
    }

    const updateScrollbar = () => {
      const tableContentWidth = Math.max(
        table.scrollWidth,
        Math.ceil(table.getBoundingClientRect().width),
      );
      const maxScrollLeft = tableWrapper.scrollWidth - tableWrapper.clientWidth;
      setHasOverflow(maxScrollLeft > 1 && tableContentWidth > tableWrapper.clientWidth);
      stickyScrollbarContent.style.width = `${tableContentWidth}px`;
      stickyScrollbar.scrollLeft = tableWrapper.scrollLeft;
    };

    const onTableScroll = () => {
      if (isSyncingRef.current) return;
      isSyncingRef.current = true;
      stickyScrollbar.scrollLeft = tableWrapper.scrollLeft;
      isSyncingRef.current = false;
    };

    const onStickyScroll = () => {
      if (isSyncingRef.current) return;
      isSyncingRef.current = true;
      tableWrapper.scrollLeft = stickyScrollbar.scrollLeft;
      isSyncingRef.current = false;
    };

    const resizeObserver = new ResizeObserver(updateScrollbar);
    resizeObserver.observe(tableWrapper);
    resizeObserver.observe(table);

    requestAnimationFrame(updateScrollbar);
    setTimeout(updateScrollbar, 0);
    window.addEventListener('resize', updateScrollbar);
    tableWrapper.addEventListener('scroll', onTableScroll, { passive: true });
    stickyScrollbar.addEventListener('scroll', onStickyScroll, { passive: true });

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateScrollbar);
      tableWrapper.removeEventListener('scroll', onTableScroll);
      stickyScrollbar.removeEventListener('scroll', onStickyScroll);
    };
  }, [isDisabled]);

  return {
    hasOverflow,
    tableRef,
    tableWrapperRef,
    stickyScrollbarRef,
    stickyScrollbarContentRef,
  };
};
